import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

export interface User {
  id: number;
  name: string;
  email?: string;
  username?: string;
  role: string;
  token?: string;
  expiresIn?: number;
}

export interface PendingLogin {
  email: string;
  user: User;
  accessToken: string;
  expiresIn: number;
}

interface AuthContextType {
  user: User | null;
  pendingLogin: PendingLogin | null;
  isHydrated: boolean;
  toast: {
    message: string;
    type: string;
  } | null;
  login: (user: User) => void;
  logout: () => void;
  setPendingLogin: (pending: PendingLogin) => void;
  completeLogin: (pending: PendingLogin) => void;
  showToast: (
    message: string,
    type?: string
  ) => void;
}

const AuthContext = createContext<AuthContextType>(
  {} as AuthContextType
);

export const AuthProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [pendingLogin, setPendingLoginState] =
    useState<PendingLogin | null>(null);
  const [isHydrated, setIsHydrated] =
    useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: string;
  } | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    const pending = sessionStorage.getItem(
      "pendingLogin"
    );

    if (stored) {
      setUser(JSON.parse(stored));
    }

    if (pending) {
      setPendingLoginState(JSON.parse(pending));
    }

    setIsHydrated(true);
  }, []);

  const login = (userData: User) => {
    localStorage.setItem(
      "user",
      JSON.stringify(userData)
    );
    sessionStorage.removeItem("pendingLogin");
    setPendingLoginState(null);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("user");
    sessionStorage.removeItem("pendingLogin");
    setUser(null);
    setPendingLoginState(null);
  };

  const setPendingLogin = (
    pending: PendingLogin
  ) => {
    sessionStorage.setItem(
      "pendingLogin",
      JSON.stringify(pending)
    );
    setPendingLoginState(pending);
  };

  const completeLogin = (
    pending: PendingLogin
  ) => {
    localStorage.setItem(
      "user",
      JSON.stringify(pending.user)
    );
    sessionStorage.removeItem("pendingLogin");
    setPendingLoginState(null);
    setUser(pending.user);
  };

  const showToast = (
    message: string,
    type = "success"
  ) => {
    setToast({
      message,
      type,
    });

    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        pendingLogin,
        isHydrated,
        toast,
        login,
        logout,
        setPendingLogin,
        completeLogin,
        showToast,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () =>
  useContext(AuthContext);
